import { Color } from '@persona-id/design-tokens';
import { ComponentPropsWithoutRef, FC } from 'react';
import styled, { keyframes } from 'styled-components';

import { HeadingSize, ParagraphSize } from 'design-system/mixins/text';

interface LoadingShimmerProps extends ComponentPropsWithoutRef<'span'> {
  height?: string;
  width?: string;
}

const shimmerKeyframes = keyframes`
  0% {
   transform: translate(-100%);
  }

  50%, 100% {
    transform: translate(100%);
  }
`;

/**
 * Base class of all Loading Shimmer components.
 */
export const LoadingShimmer = styled.span.attrs(() => ({
  'aria-live': 'polite',
  'aria-busy': 'true',
}))<LoadingShimmerProps>`
  position: relative;
  display: inline-block;
  background-color: ${Color.CoreSlate50};
  width: ${({ width = '100px' }) => width};
  height: ${({ height = 'auto' }) => height};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, ${Color.CoreSlate200} 50%, transparent 100%);
    transform: translate(-100%);
    animation-name: ${shimmerKeyframes};
    animation-duration: 1s;
    animation-timing-function: ease-in;
    animation-iteration-count: infinite;
  }
`;

interface LoadingShimmerHeadingProps extends LoadingShimmerProps {
  size?: HeadingSize;
}

export const LoadingShimmerHeading = styled(LoadingShimmer)<LoadingShimmerHeadingProps>`
  height: ${({ theme, size = 'lg' }) => theme.text.heading[size].fontSize};
`;

interface LoadingShimmerParagraphProps extends LoadingShimmerProps {
  size?: ParagraphSize;
  // eg. 200px
  width?: string;
}

export const LoadingShimmerParagraph = styled(LoadingShimmer)<LoadingShimmerParagraphProps>`
  height: ${({ theme, size = 'md' }) => theme.text.paragraph[size].fontSize};
`;

// A "maybe" shimmer block that is shimmering if the `isLoading`
// value is set to true and otherwise just displays its children
interface MaybeLoadingShimmerProps {
  isLoading: boolean;
  height?: string;
  width?: string;
}

export const MaybeLoadingShimmer: FC<MaybeLoadingShimmerProps> = ({
  isLoading,
  children,
  height,
  width,
}) => (isLoading ? <LoadingShimmer height={height} width={width} /> : <>{children}</>);
